/**
 * Mode Toggle Component
 *
 * Allows switching between Chat Mode and Expert Mode for campaign management.
 */

import React from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, Typography, Chip } from '@mui/material';
import AiChatIcon from 'features/ui/app-layout/rail/ai-chat-icon';
import TuneIcon from '@mui/icons-material/Tune';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

export type CampaignMode = 'chat' | 'expert';

interface ModeToggleProps {
  mode: CampaignMode;
  onChange: (mode: CampaignMode) => void;
  disabled?: boolean;
  compact?: boolean;
  showLabels?: boolean;
}

const ModeToggle: React.FC<ModeToggleProps> = ({
  mode,
  onChange,
  disabled = false,
  compact = false,
  showLabels = true,
}) => {
  const { classes, cx } = useStyles({ compact });
  const { t } = useTranslation();

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newMode: CampaignMode | null) => {
    if (newMode !== null) {
      onChange(newMode);
    }
  };

  return (
    <Box className={classes.container}>
      {showLabels && !compact && (
        <Typography variant="caption" className={classes.label}>
          {t('campaignMode', 'Campaign Mode')}:
        </Typography>
      )}
      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={handleChange}
        disabled={disabled}
        className={classes.toggleGroup}
        size={compact ? 'small' : 'medium'}
      >
        <ToggleButton value="chat" className={cx(classes.toggleButton, mode === 'chat' && 'active')}>
          <Tooltip title={t('chatModeDescription', 'AI-guided campaign creation with step-by-step assistance')}>
            <Box className={classes.buttonContent}>
              <AiChatIcon className={classes.icon} />
              {showLabels && <span>{t('chatMode', 'Chat')}</span>}
            </Box>
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="expert" className={cx(classes.toggleButton, mode === 'expert' && 'active')}>
          <Tooltip title={t('expertModeDescription', 'Full manual control over campaign settings')}>
            <Box className={classes.buttonContent}>
              <TuneIcon className={classes.icon} />
              {showLabels && <span>{t('expertMode', 'Expert')}</span>}
            </Box>
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

/**
 * Simplified Mode Indicator
 * Shows that the user is in simplified mode with option to switch
 */
interface SimplifiedModeIndicatorProps {
  onSwitchToFull?: () => void;
}

export const SimplifiedModeIndicator: React.FC<SimplifiedModeIndicatorProps> = ({ onSwitchToFull }) => {
  const { classes } = useStyles({});
  const { t } = useTranslation();

  return (
    <Box className={classes.indicatorContainer}>
      <Chip
        icon={<AiChatIcon />}
        label={t('simplifiedMode', 'Simplified Mode')}
        size="small"
        color="primary"
        variant="outlined"
        onClick={onSwitchToFull}
        className={classes.modeChip}
      />
    </Box>
  );
};

const useStyles = makeStyles<{ compact?: boolean }>()((theme, { compact }) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  label: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  toggleGroup: {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 8,
    padding: 2,
    '& .MuiToggleButtonGroup-grouped': {
      border: 0,
      borderRadius: 6,
      '&:not(:first-of-type)': {
        borderRadius: 6,
        marginLeft: 2,
      },
      '&:first-of-type': {
        borderRadius: 6,
      },
    },
  },
  toggleButton: {
    padding: compact ? theme.spacing(0.5, 1) : theme.spacing(0.75, 1.5),
    textTransform: 'none',
    color: theme.palette.text.secondary,
    backgroundColor: 'transparent',
    transition: 'all 0.2s',
    '&.active': {
      backgroundColor: '#fff',
      color: theme.palette.primary.main,
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.5)',
    },
    '&.Mui-selected': {
      backgroundColor: '#fff',
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: '#fff',
      },
    },
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  icon: {
    fontSize: compact ? '1rem' : '1.25rem',
  },
  indicatorContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  modeChip: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.main + '10',
    },
  },
}));

export default ModeToggle;

